import { Button, Typography } from '@/ui';

import type {
  MemberSeatChange,
  MemberSeatChangePhase,
} from '@/types/memberSeatChange';

import { formatSeatChangeSchedule } from '@/components/Profile/Client/SeatChangeReview';
import { useGetMemberSeatChangesQuery, userApi } from '@/store/api/userApi';
import { validSeatChangeSchedule } from '@/types/seatChangePreview';

const phaseMessages: Record<MemberSeatChangePhase, string> = {
  captured: 'Seat saved; calendar updates in progress',
  submitting: 'Seat saved; calendar updates in progress',
  remote_pending: 'Seat saved; calendar updates in progress',
  platform_pending: 'Calendar confirmed; session updates in progress',
  preflight_blocked: 'Calendar update blocked; manually check the calendar',
  manual_review: 'Calendar update failed; manually check the calendar',
  completed: 'Calendar and session updates completed',
  completed_noop: 'Seat saved; no eligible future sessions to update',
  resolved_no_remote_effect: 'Resolved without calendar changes',
  rejected_no_effect: 'Calendar update rejected; no calendar changes applied',
  resolved_with_exclusions:
    'Calendar update resolved with exclusions; review excluded sessions',
};

const activePhases: MemberSeatChangePhase[] = [
  'captured',
  'submitting',
  'remote_pending',
  'platform_pending',
];

const terminalPhases: MemberSeatChangePhase[] = [
  'completed',
  'completed_noop',
  'resolved_no_remote_effect',
  'rejected_no_effect',
  'resolved_with_exclusions',
];
const recoveryMessages = {
  scheduled: 'Calendar recovery scheduled; verification is pending',
  manual_action: 'Manual calendar action required; contact support',
  exhausted: 'Automatic calendar recovery exhausted; contact support',
};
const reasonMessages: Record<string, string> = {
  conflict: 'Calendar conflict',
  availability_conflict:
    'Calendar or buffer conflict. Review the affected date before requesting another review.',
  overlap_conflict:
    'Actual calendar overlap. Resolve the conflict before requesting another review.',
  buffer_conflict:
    'Buffer-only conflict. A fresh review and explicit buffer approval are required.',
  availability_unavailable:
    'Calendar availability could not be verified. Check calendar access and contact support.',
  projection_identity_or_version_conflict:
    'Session verification found changed data. Contact support.',
  projection_pending: 'Session verification is pending.',
  recovery_exhausted: 'Automatic recovery attempts exhausted. Contact support.',
  authorization_denied:
    'Operator authorization could not be verified. Contact support.',
  calendar_update_requires_attention:
    'Calendar update requires attention; contact support.',
};
const safeReason = (code?: string | null) => {
  const message = code ? reasonMessages[code] : undefined;
  return typeof message === 'string'
    ? message
    : 'Calendar update requires attention; contact support.';
};
const isTerminal = (operation: MemberSeatChange) =>
  terminalPhases.includes(operation.phase);
const operationMessage = (operation: MemberSeatChange) => {
  if (!isTerminal(operation) && operation.recovery) {
    return (
      recoveryMessages[operation.recovery.status] ||
      'Calendar recovery status is unavailable; contact support'
    );
  }
  return phaseMessages[operation.phase];
};

const SeatChangeStatus = ({ memberId }: { memberId: string }) => {
  const cached = userApi.endpoints.getMemberSeatChanges.useQueryState(
    memberId,
    { skip: !memberId },
  );
  const pollingInterval = cached.currentData?.operations.some(
    (operation) =>
      activePhases.includes(operation.phase) ||
      (!isTerminal(operation) &&
        (operation.recovery?.status === 'scheduled' ||
          operation.projectionPhase === 'pending' ||
          ['queued', 'running', 'retry_wait'].includes(
            operation.activeReconciliation?.status ?? '',
          ))) ||
      operation.emailState === 'pending' ||
      operation.emailState === 'failed',
  )
    ? 5000
    : 0;
  const { currentData, isError, isFetching, refetch } =
    useGetMemberSeatChangesQuery(memberId, {
      skip: !memberId,
      refetchOnMountOrArgChange: true,
      pollingInterval,
    });

  if (!memberId || (!isError && !currentData?.operations.length)) return null;

  return (
    <section
      aria-label="Seat-change status"
      aria-live="polite"
      className="container mx-auto max-w-screen-lg px-6 py-4"
    >
      {isError && (
        <Typography role="alert">Unable to load seat-change status.</Typography>
      )}
      {currentData?.operations.map((operation) => (
        <div
          key={operation.id}
          className="mb-3 rounded-xl border border-solid border-cement-200 bg-white p-4"
        >
          <Typography variant="subtitle1">
            {operationMessage(operation)}
          </Typography>
          {!isTerminal(operation) &&
            operation.counts.applied + operation.counts.alreadyTarget > 0 && (
              <Typography>
                {operation.counts.applied + operation.counts.alreadyTarget}{' '}
                session durations applied or already at target; final
                verification pending
              </Typography>
            )}
          {!isTerminal(operation) &&
            operation.activeReconciliation &&
            ['queued', 'running', 'retry_wait'].includes(
              operation.activeReconciliation.status,
            ) && (
              <Typography>
                Operator review{' '}
                {operation.activeReconciliation.status === 'queued'
                  ? 'queued'
                  : 'in progress'}
                ; this is not a completed calendar update
              </Typography>
            )}
          {!isTerminal(operation) && operation.recovery?.failureCode && (
            <Typography role="alert">
              {safeReason(operation.recovery.failureCode)}
            </Typography>
          )}
          {!!operation.occurrences?.length && (
            <details className="my-3" open={operation.phase !== 'completed'}>
              <summary className="cursor-pointer font-medium">
                Session change details
              </summary>
              {operation.occurrences.map((occurrence) => (
                <div key={occurrence.appointmentId} className="my-3">
                  {validSeatChangeSchedule(occurrence.originalSchedule) && (
                    <Typography>
                      Original:{' '}
                      {formatSeatChangeSchedule(occurrence.originalSchedule)}
                    </Typography>
                  )}
                  {validSeatChangeSchedule(occurrence.targetSchedule) && (
                    <Typography>
                      Target:{' '}
                      {formatSeatChangeSchedule(occurrence.targetSchedule)}
                    </Typography>
                  )}
                  {occurrence.reason && (
                    <Typography>{safeReason(occurrence.reason)}</Typography>
                  )}
                </div>
              ))}
            </details>
          )}
          <Typography>
            {operation.previousSeatType} to {operation.targetSeatType}:{' '}
            {operation.effectiveAt}
          </Typography>
          <Typography>
            {operation.counts.applied + operation.counts.alreadyTarget} of{' '}
            {operation.counts.affected} sessions confirmed;{' '}
            {operation.counts.unresolved} unresolved;{' '}
            {operation.counts.excluded} excluded
          </Typography>
          {operation.actor.name && (
            <Typography>Saved by {operation.actor.name}</Typography>
          )}
          {operation.error && (
            <Typography role="alert">
              {safeReason(operation.error.code)}
            </Typography>
          )}
          {operation.emailState === 'pending' && (
            <Typography>Notification emails pending</Typography>
          )}
          {operation.emailState === 'accepted' && (
            <Typography>
              Notification emails accepted by the email provider
            </Typography>
          )}
          {operation.emailState === 'failed' && (
            <Typography role="alert">
              Notification email failed; calendar status is unchanged
            </Typography>
          )}
        </div>
      ))}
      <Button
        disabled={isFetching}
        variant="outlined"
        onClick={() => {
          if (!isFetching) refetch();
        }}
      >
        Refresh status
      </Button>
    </section>
  );
};

export default SeatChangeStatus;
